/**
 * Workspace Restore Modal (Spec 205 — US2)
 *
 * Launch a workspace-complete restore of a definition point into a target workspace.
 * AppModal (movable, no outside-dismiss per Principle XLV). Data points are passed through
 * when the caller supplies them (the atomic run restores data in-process, then definitions).
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 205-workspace-complete-dr
 */

import React, { useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import { Button, Input, Badge } from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import { useLaunchWorkspaceRestore } from '../../hooks/useWorkspaceRestore';
import {
  WorkspaceRestoreCandidate,
  WorkspaceRestoreDataPoint,
} from '../../api/workspaceRestore';

interface Props {
  open: boolean;
  onClose: () => void;
  candidate: WorkspaceRestoreCandidate;
  dataPoints?: WorkspaceRestoreDataPoint[];
}

export const WorkspaceRestoreModal: React.FC<Props> = ({ open, onClose, candidate, dataPoints = [] }) => {
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('');
  const launch = useLaunchWorkspaceRestore();
  const point = candidate.latestDefinitionPoint;

  const onLaunch = () => {
    if (!point || !targetWorkspaceId) return;
    launch.mutate(
      {
        workspaceId: candidate.workspaceId,
        definitionRunId: point.definitionRunId,
        targetWorkspaceId: targetWorkspaceId.trim(),
        dataPoints,
      },
      { onSuccess: () => onClose() }
    );
  };

  const refused =
    launch.data?.status === 'refused' ? launch.data.reason ?? 'Pre-flight validation failed' : null;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Restore workspace — ${candidate.workspaceName}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={onLaunch}
            disabled={!point || !targetWorkspaceId.trim() || launch.isPending}
          >
            {launch.isPending ? 'Launching…' : 'Launch restore'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 p-4">
        {!point ? (
          <p className="text-[var(--color-text-secondary)]">
            This workspace has no restorable definition recovery point.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <span>Definition point {formatEastern(point.pointTime)}</span>
              <Badge variant="success">{point.supportedCount} items</Badge>
              {point.unsupportedCount > 0 && (
                <Badge variant="warning">{point.unsupportedCount} unsupported</Badge>
              )}
              {point.failedCount > 0 && <Badge variant="danger">{point.failedCount} failed</Badge>}
            </div>
            <Input
              label="Target workspace ID"
              value={targetWorkspaceId}
              onChange={(e) => setTargetWorkspaceId(e.target.value)}
              placeholder="A new/empty Fabric workspace GUID"
            />
            <p className="text-xs text-[var(--color-text-secondary)]">
              {dataPoints.length > 0
                ? `${dataPoints.length} data point(s) will be restored, then the definitions recreated and re-pointed.`
                : 'Definitions-only restore — references resolve to the target workspace where they map; the rest are reported unresolved.'}
            </p>
            {refused && <p className="text-sm text-[var(--color-status-danger,red)]">{refused}</p>}
          </>
        )}
      </div>
    </AppModal>
  );
};

export default WorkspaceRestoreModal;
